/**
 * MagicLinkDobForm — shared form sub-component for magic-link auth.
 *
 * Used by:
 *   - MagicLinkLandingPage (catch-all route /<token>) — token captured
 *     from URL, hidden from the user (only DOB input visible)
 *   - ManualLoginPage (/login) — token visible, manually entered by
 *     the patient (lost SMS, typed URL from memory, etc.)
 *
 * The two cases share everything else: same endpoint, same error
 * handling, same success flow. The only difference is whether the
 * `token` field is rendered as a visible input or kept as component
 * state.
 *
 * Per decision ledger #41: this component issues the POST request that
 * consumes the token. The parent page MUST NOT trigger verifyMagicLink
 * outside of this submit handler — preview-bot crawlers GET the URL
 * before patients do, and any GET-side consumption would lock tokens
 * before patients can use them. The token capture happens at parent
 * render time (from URL or from the visible input); consumption happens
 * here on submit.
 */
import { useState, type FormEvent } from 'react';
import { z } from 'zod';

import { Alert } from '@tensaw/design-system/feedback';

import { ApiError } from '../../api/client';
import { useVerifyMagicLink } from '../../api/queries';
import type { MagicLinkResponse } from '../../api/types';
import { dobIsoSchema } from '../../lib/dob';
import { LOGIN_THEME } from '../../lib/loginTheme';
import { Dob3BoxInput } from './Dob3BoxInput';

interface MagicLinkDobFormProps {
  /**
   * Initial token value. From the URL path for the landing page; empty
   * string for the manual-entry page (patient types it in).
   */
  initialToken: string;
  /**
   * When false, the token input is visible and editable. When true, the
   * token is locked to `initialToken` and the input is hidden.
   *
   * Landing page passes `true` (token already captured from URL);
   * manual page passes `false` (patient needs to type the token).
   */
  hideTokenInput: boolean;
  /**
   * Called with the success response after the patient submits and the
   * backend returns 200. Parent navigates per `redirect_path`.
   */
  onSuccess: (response: MagicLinkResponse) => void;
}

// Validation is intentionally lenient — backend normalizes the token
// (strips non-alphanumerics, uppercases) and validates server-side. The
// only client-side constraints are non-empty + within Pydantic's 1-32
// chars + DOB shape. Anything tighter would catch typos AFTER the
// patient hits submit, which is worse UX than trusting the backend's
// generic error code.
const DobSchema = z.object({
  token: z.string().trim().min(1, 'Please enter your link code').max(32),
  // DOB is the exception to the leniency above: it is collected as three
  // separate numeric boxes, so an impossible date (month 99, Feb 30, a
  // year in the future) is a client-side typo we can name precisely
  // instead of forwarding it to the backend's generic failure code.
  dob: dobIsoSchema,
});

export function MagicLinkDobForm({
  initialToken,
  hideTokenInput,
  onSuccess,
}: MagicLinkDobFormProps) {
  const [token, setToken] = useState(initialToken);
  const [dob, setDob] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  // Blur-time DOB message, kept separate from `validationError` so leaving
  // the DOB row does not wipe a token complaint from the last submit. Both
  // render through the single Alert below.
  const [dobError, setDobError] = useState<string | null>(null);
  const verify = useVerifyMagicLink();

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setValidationError(null);
    setDobError(null);

    const parsed = DobSchema.safeParse({ token: token.trim(), dob });
    if (!parsed.success) {
      // First error wins — the patient only needs one message at a time.
      const firstIssue = parsed.error.issues[0];
      setValidationError(firstIssue?.message ?? 'Please check the form');
      return;
    }

    verify.mutate(
      {
        token: parsed.data.token,
        patient_dob: parsed.data.dob,
        client_ip: 'unknown',
        client_user_agent: 'unknown',
      },
      {
        onSuccess,
      },
    );
  };

  // Backend returns a single error code for all failure modes per
  // UI_DATA_LAYER §1b — anti-enumeration. We show one generic copy.
  // The correlation_id from the response goes to logs for support to
  // drill into; we don't surface it in the UI.
  const backendErrorMessage =
    verify.isError && verify.error instanceof ApiError
      ? verify.error.problem.detail ||
        'Sign-in failed. Please check your link and date of birth.'
      : verify.isError
      ? 'Something went wrong. Please try again.'
      : null;

  // One error message, not two: the submit failure if there is one,
  // otherwise whatever the DOB field reported when the patient left it.
  const formError = validationError ?? dobError;

  const labelClass = LOGIN_THEME === 'iris' ? 'ir-field-label' : 'pr-field-label';
  const hintClass = LOGIN_THEME === 'iris' ? 'ir-field-hint' : 'pr-field-hint';
  const inputClass = LOGIN_THEME === 'iris' ? 'ir-input ir-input-pin' : 'pr-input pr-input-pin';
  const fieldClass = LOGIN_THEME === 'iris' ? 'ir-field' : 'pr-field';

  return (
    <form onSubmit={submit} noValidate className="flex flex-col gap-4">
      {!hideTokenInput && (
        <div className={fieldClass}>
          <label htmlFor="field-token" className={labelClass}>Link code</label>
          <input
            id="field-token"
            type="text"
            inputMode="text"
            autoComplete="one-time-code"
            placeholder="XXXX-XXXX-XXXX"
            className={inputClass}
            value={token}
            onChange={(e) => { setToken(e.target.value); }}
            aria-describedby="token-helper"
          />
          <span id="token-helper" className={hintClass}>
            The code from your text message. Dashes and capitalization
            don&rsquo;t matter.
          </span>
        </div>
      )}

      <div className={fieldClass}>
        <label htmlFor="dob-m" className={labelClass}>Date of birth</label>
        <Dob3BoxInput
          theme={LOGIN_THEME}
          value={dob}
          onChange={setDob}
          onValidate={setDobError}
          error={Boolean(formError)}
        />
      </div>

      {/* Surface validation errors above the backend error so both can show
          if the patient hits a Zod error first, then a server error. */}
      {formError && (
        <Alert variant="error" title="Check your details">
          {formError}
        </Alert>
      )}

      {backendErrorMessage && (
        <Alert variant="error" title="Sign-in failed">
          {backendErrorMessage}
        </Alert>
      )}

      <button
        type="submit"
        className={LOGIN_THEME === 'iris' ? 'ir-btn-primary' : 'pr-btn-primary'}
        disabled={verify.isPending}
      >
        {verify.isPending ? 'Verifying...' : 'Continue'}
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M6 3l5 5-5 5" />
        </svg>
      </button>
    </form>
  );
}
